import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile, useUSDT, useTx } from "../hooks/useContract";
import { useNotification } from "../hooks/useNotification";
import { PACKAGES, DEFAULT_REFERRER_ID } from "../config/constants";
import { CONTRACT_ADDRESS, LOGIC_ADDRESS } from "../config/contract";
import { formatUSDT, stringToBytes32, bytes32ToString, validateNameForContract } from "../utils/format";
import { parseError } from "../utils/errorParser";
import { recordOfferEvent } from "../hooks/useOffers";
import Button from "../components/Common/Button";

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notify = useNotification();
  const { account, isCorrectChain, connect, getContract, getLogic, getUsdt } = useWeb3();
  const { isRegistered, reload: reloadProfile } = useUserProfile();
  const { balance: usdtBalance, allowance, reload: reloadUsdt } = useUSDT();
  const { pending } = useTx();

  const [name, setName] = useState("");
  const [referrerId, setReferrerId] = useState(String(DEFAULT_REFERRER_ID));
  const [referrerName, setReferrerName] = useState("");
  const [referrerLookupState, setReferrerLookupState] = useState("idle"); // "idle" | "loading" | "found" | "notfound"
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(""); // "approving" | "registering"

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferrerId(ref);
  }, [searchParams]);

  useEffect(() => {
    if (isRegistered) navigate("/home", { replace: true });
  }, [isRegistered, navigate]);

  // Lookup referrer name whenever referrerId changes (debounced)
  useEffect(() => {
    const contract = getContract();
    if (!contract) return;
    const idStr = String(referrerId || "").trim();
    if (!idStr || isNaN(Number(idStr)) || Number(idStr) <= 0) {
      setReferrerName("");
      setReferrerLookupState("idle");
      return;
    }
    let cancelled = false;
    setReferrerLookupState("loading");
    const t = setTimeout(async () => {
      try {
        const u = await contract.users(idStr);
        if (cancelled) return;
        // If wallet is zero address, referrer doesn't exist
        const zero = "0x0000000000000000000000000000000000000000";
        if (!u.wallet || u.wallet.toLowerCase() === zero) {
          setReferrerName("");
          setReferrerLookupState("notfound");
        } else {
          setReferrerName(bytes32ToString(u.name));
          setReferrerLookupState("found");
        }
      } catch {
        if (!cancelled) {
          setReferrerName("");
          setReferrerLookupState("notfound");
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrerId, account]);

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">Connect your wallet to continue.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }

  const price1 = ethers.utils.parseUnits(String(PACKAGES[0].price), 18);
  const hasBalance = usdtBalance.gte(price1);

  // Live name validation — mirrors the contract's _validateName rules exactly.
  const nameErr = name ? validateNameForContract(name) : null;

  // Single button: approve if needed, then register
  const oneClickJoin = async () => {
    if (nameErr) return notify.error(nameErr);
    if (!name.trim()) return notify.error("Please enter your name");
    if (!referrerId || isNaN(Number(referrerId))) return notify.error("Invalid referrer ID");
    if (!hasBalance) return notify.error("Not enough USDT in wallet");

    const logic = getLogic();
    const storage = getContract();
    const usdt = getUsdt();
    if (!logic || !usdt || !storage) return notify.error("Wallet not ready — contracts not configured");

    // Guard against unconfigured Logic address (all-zero)
    if (!LOGIC_ADDRESS || /^0x0+$/i.test(LOGIC_ADDRESS)) {
      return notify.error("Logic contract address not set. Ask the owner to configure it.");
    }

    setBusy(true);
    try {
      // ── Preflight: verify Storage↔Logic linkage on-chain ──────────
      try {
        const wiredLogic = await storage.logicContract();
        if (wiredLogic.toLowerCase() !== LOGIC_ADDRESS.toLowerCase()) {
          notify.error("Storage contract is not linked to this Logic. Ask the owner to run setLogicContract().");
          setBusy(false);
          setStep("");
          return;
        }
      } catch {
        notify.error("Cannot read Storage.logicContract() — check the Storage address.");
        setBusy(false);
        setStep("");
        return;
      }

      // ── Preflight: referrer must exist ────────────────────────────
      try {
        const refUser = await storage.users(referrerId);
        const zero = "0x0000000000000000000000000000000000000000";
        if (!refUser.wallet || refUser.wallet.toLowerCase() === zero) {
          notify.error(`Referrer #${referrerId} does not exist.`);
          setBusy(false);
          setStep("");
          return;
        }
      } catch {
        notify.error("Failed to look up referrer.");
        setBusy(false);
        setStep("");
        return;
      }

      // ── Preflight: not already registered ─────────────────────────
      const existingId = await storage.addressToId(account);
      if (existingId && existingId.gt && existingId.gt(0)) {
        notify.error("This wallet is already registered.");
        setBusy(false);
        setStep("");
        return;
      }

      // ── Re-check USDT balance FRESH from chain (cached balance may be stale)
      let freshBalance;
      try {
        freshBalance = await usdt.balanceOf(account);
      } catch {
        notify.error("Failed to read USDT balance.");
        setBusy(false);
        setStep("");
        return;
      }
      if (freshBalance.lt(price1)) {
        const have = ethers.utils.formatUnits(freshBalance, 18);
        notify.error(`Not enough USDT — you have $${Number(have).toFixed(2)}, need $${PACKAGES[0].price}.`);
        setBusy(false);
        setStep("");
        return;
      }

      // ── Approve USDT to Storage (where funds actually land) ───────
      // Re-check allowance from chain (approval may have been done in another tab)
      const freshAllowance = await usdt.allowance(account, CONTRACT_ADDRESS);
      if (freshAllowance.lt(price1)) {
        setStep("approving");
        notify.info("Approving USDT...");
        const approveTx = await usdt.approve(CONTRACT_ADDRESS, ethers.constants.MaxUint256);
        await approveTx.wait();
        notify.success("Approved");
      }

      // ── Encode the name (throws with a clear message if invalid) ───
      let nameBytes;
      try {
        nameBytes = stringToBytes32(name);
      } catch (nameE) {
        notify.error(nameE.message || "Invalid name");
        setBusy(false);
        setStep("");
        return;
      }

      // ── Simulate register call — surfaces the actual revert reason
      //    (custom error selector, or ERC20 string) instead of `0x` on failure.
      try {
        await logic.callStatic.register(referrerId, nameBytes);
      } catch (simErr) {
        notify.error(parseError(simErr));
        setBusy(false);
        setStep("");
        return;
      }

      // ── Send the real register tx ─────────────────────────────────
      setStep("registering");
      notify.info("Registering...");
      const regTx = await logic.register(referrerId, nameBytes);
      const regReceipt = await regTx.wait();
      notify.success("Welcome to 888 Global!");
      // Record for any active reward offer (fire-and-forget)
      recordOfferEvent({
        wallet: account,
        name: name.trim(),
        action: "register",
        packageId: 1, // register auto-activates P1
        amount: String(PACKAGES[0].price),
        txHash: regReceipt?.transactionHash || regTx?.hash || "",
      });
      reloadProfile();
      reloadUsdt();
      navigate("/home", { replace: true });
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(false);
      setStep("");
    }
  };

  return (
    <div className="animate-fade-in-up">
      <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
        <img
          src="/logo.png"
          alt=""
          style={{
            width: 68,
            height: 68,
            filter: "drop-shadow(0 0 16px rgba(0, 229, 255, 0.6))",
          }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <h1 style={{ fontSize: 32, marginTop: 12, background: "var(--grad-cyber)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          Create Account
        </h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 6 }}>
          Register with a referrer to activate Package 1
        </p>
      </div>

      <div className="card mb-lg animate-fade-in-scale">
        <div className="input-group">
          <label className="input-label">Your Name</label>
          <input
            type="text"
            className="input"
            placeholder="e.g. John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={31}
            disabled={busy}
            style={nameErr ? { borderColor: "var(--error)" } : undefined}
          />
          <div style={{ marginTop: 6, minHeight: 18, fontSize: 11, display: "flex", justifyContent: "space-between", gap: 6 }}>
            <span style={{ color: nameErr ? "var(--error)" : "var(--text-muted)", flex: 1 }}>
              {nameErr || "Plain ASCII only — letters, numbers, spaces, basic symbols."}
            </span>
            <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              {name.trim().length}/31
            </span>
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Referrer ID</label>
          <input
            type="number"
            className="input"
            placeholder="Referrer ID"
            value={referrerId}
            onChange={(e) => setReferrerId(e.target.value)}
            disabled={busy}
          />
          {/* Sponsor name preview */}
          <div style={{ marginTop: 6, minHeight: 18, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            {referrerLookupState === "loading" && (
              <span style={{ color: "var(--text-muted)" }}>Looking up sponsor…</span>
            )}
            {referrerLookupState === "found" && (
              <>
                <span style={{ color: "var(--text-muted)" }}>Sponsor:</span>
                <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                  {referrerName || "Unnamed"}
                </span>
                <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                  #{referrerId}
                </span>
              </>
            )}
            {referrerLookupState === "notfound" && (
              <span style={{ color: "var(--error)" }}>Sponsor ID not found</span>
            )}
          </div>
        </div>

        <div className="stat-grid mb-lg" style={{ marginTop: 12 }}>
          <div className="stat-card">
            <div className="stat-card-label">Cost</div>
            <div className="stat-card-value" style={{ color: "var(--cyan)" }}>
              ${PACKAGES[0].price}
            </div>
            <div className="stat-card-sub">USDT · Package 1</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Your Balance</div>
            <div className="stat-card-value" style={{ color: hasBalance ? "var(--success)" : "var(--error)" }}>
              ${formatUSDT(usdtBalance)}
            </div>
            <div className="stat-card-sub">USDT available</div>
          </div>
        </div>

        <Button
          variant="primary"
          size="lg"
          full
          loading={busy}
          disabled={!hasBalance || !name.trim() || !!nameErr || !referrerId || referrerLookupState === "notfound" || referrerLookupState === "loading"}
          onClick={oneClickJoin}
        >
          {busy && step === "approving"
            ? "Approving USDT..."
            : busy && step === "registering"
            ? "Registering..."
            : "Approve & Register"}
        </Button>
      </div>

      <div className="section-header">
        <div className="section-title">What You Get</div>
      </div>
      <div className="card">
        <div className="list-item">
          <div className="list-item-icon">01</div>
          <div className="list-item-body">
            <div className="list-item-title">Unique User ID</div>
            <div className="list-item-sub">Auto-generated on-chain</div>
          </div>
        </div>
        <div className="list-item">
          <div className="list-item-icon">02</div>
          <div className="list-item-body">
            <div className="list-item-title">Package 1 Activated</div>
            <div className="list-item-sub">$8 tier — starts your income</div>
          </div>
        </div>
        <div className="list-item">
          <div className="list-item-icon">03</div>
          <div className="list-item-body">
            <div className="list-item-title">Referral Link</div>
            <div className="list-item-sub">Share to earn 45% direct commissions</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
